import { useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { redirectToLogin } from '../../api/client';
import { isTokenValid } from '../../utils';

/**
 * The session guard. Every protected route sits inside it — structurally, as
 * a pathless layout route in src/index.js, so a route added to that group is
 * protected by default (src/__tests__/appRoutes.test.js walks the route table
 * in docs/ARCHITECTURE.md and fails on a non-public route outside it).
 *
 * No session — no token, or a token whose `exp` has passed — sends the person
 * to sign in and back (`/login?isRedirect=true&returnPath=<here>`) BEFORE the
 * page renders: the guard renders nothing, so no form is shown and nothing
 * typed is lost. It does not wait for a request to 401. A token that looks
 * valid but the API rejects (revoked, signed out everywhere) is still the
 * 401 interceptor's (src/api/client.js).
 *
 * The session is read when a path is entered, not on every render: a sign-out
 * that happens ON a protected page (Profile's "Sign out everywhere", account
 * deletion) clears the token and then navigates on by itself, and the guard
 * must not race it to /login with that page as the return path. The next
 * path entered is checked again.
 *
 * As a layout route it renders the matched child (<Outlet />); as a wrapper
 * (RequireAdmin, for an expired token) it renders its children.
 */
export default function RequireSession({ children }) {
	const { pathname, search } = useLocation();

	const entry = useRef(null);
	if (!entry.current || entry.current.pathname !== pathname) {
		entry.current = { pathname, signedIn: isTokenValid() };
	}
	const { signedIn } = entry.current;

	useLayoutEffect(() => {
		if (!signedIn) redirectToLogin(`${pathname}${search}`);
	}, [signedIn, pathname, search]);

	if (!signedIn) return null;
	return children === undefined ? <Outlet /> : children;
}
